<template>
  <div class="list-name-cell" :class="`list-name-cell--${align}`">
    <router-link
      v-if="to"
      :to="to"
      class="btn btn-link text-wrap text-left p-0"
    >{{ label }}</router-link>
    <span v-else class="text-wrap">{{ label }}</span>

    <span v-if="$scopedSlots.badges || $slots.badges" class="list-name-cell__badges">
      <slot name="badges" />
    </span>
  </div>
</template>

<script>
/**
 * The "name" column of a server-list page (Horses / Drafts / Members …): a link to the record's
 * profile plus a wrapping group of status badges. Centralises two things every list page kept
 * re-doing by hand:
 *  - the flex layout (name + badges on one line, badges wrap under the name when narrow);
 *  - neutralising the global `.badge { min-width: 58px }`, which otherwise pads short labels
 *    ("Active", "Published", "Final") and centres them so the badge looks detached from the name.
 *
 * `align="right"` pushes the badge group to the far side of the cell (Horses); the default
 * keeps it tucked against the name (Drafts).
 *
 *   <list-name-cell :label="row.name" :to="{ name: 'Events', params: { draftId: String(row.id) } }">
 *     <template #badges>
 *       <span class="badge alert-success">Published</span>
 *     </template>
 *   </list-name-cell>
 */
export default {
  name: 'ListNameCell',
  props: {
    label: { type: [String, Number], default: '' },
    // vue-router `to` — omit for a plain (non-linked) name
    to: { type: [Object, String], default: null },
    // where the badge group sits: 'left' (against the name) | 'right' (far edge of the cell)
    align: { type: String, default: 'left' }
  }
}
</script>

<style>
/* Not scoped: the `.badge` reset below has to reach slotted badge markup. */
.list-name-cell {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 6px;
  row-gap: 2px;
}

/* `space-between`, not `margin-left: auto` on the badges — auto-margin pushes the badge group
   to the line's end even when it wraps onto its own line alone. `space-between` collapses to
   flex-start for a single item on a line, so wrapped badges sit left; badges sharing the line
   with the name still land flush right, same as before. */
.list-name-cell--right {
  justify-content: space-between;
}

.list-name-cell__badges {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 4px;
}

/* Slot present but its badges are all v-if'd off — don't leave a phantom gap after the name. */
.list-name-cell__badges:empty {
  display: none;
}

.list-name-cell__badges .badge {
  min-width: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/ListNameCell.vue